import {Component, DestroyRef, inject, Inject, OnInit} from '@angular/core';
import {FormControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemType} from '@emm/core/models/item-type.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemService} from '@emm/core/services/item.service';
import {TagService} from '@emm/core/services/tag.service';
import { Attribute } from '@emm/core/models/attribute.model';

const BOOL = 'BOOL';
const MULTI_SELECT = 'MULTI_SELECT';
const TEXT = 'TEXT';

@Component({
    selector: 'emm-add-item-dialog',
    styleUrls: ['./add-item-dialog.component.scss'],
    templateUrl: './add-item-dialog.component.html'
})
export class AddItemDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    display = 'name';
    firstFormGroup: FormGroup;
    globalAttributes: Attribute[];
    isLinear = true;
    isReadOnly$ = this.authFacade.isReadOnly$;
    item = { tag: '', name: '' };
    itemTypes: ItemType[];
    key = 'tag';
    secondFormGroup: FormGroup;
    showCreateItem = false;

    constructor(
        private authFacade: AuthFacadeService,
        public dialogRef: MatDialogRef<AddItemDialogComponent>,
        private fb: FormBuilder,
        private itemService: ItemService,
        private tagService: TagService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
        this.showCreateItem = this.data.showCreateItem;
    }



    // This add component is now reused by 2 business functions (Add Item and Add Item Grouping),
    // which have different data sets for item types.
    public ngOnInit() {
        this.dialogRef.updatePosition({ top: '100px' });

        if (this.showCreateItem) {
            this.itemTypes = this.data.itemTypes.filter(itemType => itemType.tag !== 'SUBMENU');
            this.firstFormGroup = this.fb.group({
                itemType: new FormControl(''),
                locked: new FormControl('', [Validators.required]),
                tag: new FormControl('')
            });
            this.globalAttributes = this.data.globalAttributes;
            this.buildGlobalAttributes();
            return;
        }

        this.item.tag = this.data.item.tag;
    }



    buildGlobalAttributes(): void {
        const formGroup = {};

        Object.keys(this.globalAttributes).forEach(prop => {
            const { defaultValue, tag, type } = this.globalAttributes[prop];
            switch (type) {

                case BOOL:
                    // type changed from string ('true') to boolean (true)
                    formGroup[tag] = new FormControl(defaultValue);
                    break;

                case MULTI_SELECT:
                    // no default value for multi-select
                    formGroup[tag] = new FormControl();
                    break;

                case TEXT:
                    formGroup[tag] = new FormControl(defaultValue || '', this.noWhitespaceValidator);
                    break;

                default:
                    formGroup[tag] = new FormControl(defaultValue || '');
            }
        });

        this.secondFormGroup = this.fb.group(formGroup);
    }



    findTagExists(): void {
        if (this.firstFormGroup.controls['tag'].valid) {
            this.tagService.getItemTags(this.firstFormGroup.get('tag').value)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((res) => {
                if (res.response === true) {
                    this.firstFormGroup.controls['tag'].setErrors({ tagExists: true });
                }
            });
        }
    }



    numberValidation(defaultValue): void {
        if (this.secondFormGroup.controls[defaultValue].value
            && this.secondFormGroup.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp('^[0-9]+(.[0-9]{1,2})?$');

            if (pattern.test(this.secondFormGroup.controls[defaultValue].value)) {
                this.secondFormGroup.controls[defaultValue].setErrors(null);
                return;
            }

            this.secondFormGroup.controls[defaultValue].setErrors({ pattern: true });
        }
    }


    imageValidation(defaultValue): void {
        if (this.secondFormGroup.controls[defaultValue].value
            && this.secondFormGroup.controls[defaultValue].value !== 'null') {

            const pattern = new RegExp(
                '^https?:\\/\\/' +                              // Match http:// or https://
                '[-a-zA-Z0-9@:%._\\+~#=]{2,256}' +              // Strict domain segment
                '\\.[a-z]{2,6}\\b' +                            // Top-level domain (e.g., .com, .net)
                '([-a-zA-Z0-9@:%_\\+.~#?&/=]*)' +               // Optional path
                '\\.(webp|png|jpg|jpeg|gif)' +                      // File extension check
                '(\\?.*)?(#.*)?$',                              // Optional query and fragment
                'i'                                             // Case-insensitive
            );


            if (pattern.test(this.secondFormGroup.controls[defaultValue].value)) {
                this.secondFormGroup.controls[defaultValue].setErrors(null);
                return;
            }

            this.secondFormGroup.controls[defaultValue].setErrors({ pattern: true });
        }
    }



    closeAfterSave(): void {
        this.dialogRef.close('save');
    }



    onNoClick(): void {
        this.dialogRef.close();
    }



    onSubmit(): void {
        if (this.firstFormGroup.valid) {
            this.dialogRef.updatePosition({ top: '50px' });
        }
    }



    onSubmit2(): void {
        if (this.firstFormGroup.valid && this.secondFormGroup.valid) {
            const item = {
                attributes: this.getAttributeModifications(),
                itemType: this.firstFormGroup.controls['itemType'].value,
                locked: this.firstFormGroup.controls['locked'].value,
                tag: this.firstFormGroup.controls['tag'].value
            };
            this.itemService.saveItem(item)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(() => {
                this.closeAfterSave();
            });
        }
    }



    // save global attributes
    getAttributeModifications(): Attribute[] {
        const globalAttributes = [];
        for (const attribute of this.globalAttributes) {
            const ga = {}; // itemAttribute
            const formValue = this.secondFormGroup.get(attribute.tag).value;

            if (!this.secondFormGroup.controls[attribute.tag].dirty) {
                continue;
            }

            // limit request to only these fields instead of the entire nested objects
            const tagSplit = attribute.tag.split('|');
            ga['tag'] = tagSplit[0];
            ga['value'] = formValue;
            globalAttributes.push(ga);
        }
        return globalAttributes;
    }



    noWhitespaceValidator(control: FormControl): {} {
        if (control.value.startsWith(' ') || control.value.endsWith(' ')) {
            return { invalidWhiteSpace: 'Invalid white space' };
        }
    }



}
